<template>
  <div
    :class="blockClasses"
  >
    <slot
      name="label"
      v-if="!editable"
    >
      <span
        v-if="!isTextArea"
        v-html="valueToShow"
      />
      <div
        v-else
        class="label-edit__textarea-label rounded border"
        :class="textAreaLabelClasses"
        v-html="valueToShow"
      />
    </slot>
    <slot v-else>
      <b-input
        v-if="!isTextArea"
        :type="inputType"
        v-model="valueProxy"
        v-bind="inputOptions"
        v-on="inputEvents"
      />
      <b-form-textarea
        v-else
        v-model="valueProxy"
        v-bind="textAreaInputOptions"
        v-on="inputEvents"
      />
    </slot>
  </div>
</template>
<style lang="sass" scoped>
$field-height: calc(1.5em + 0.75rem + 2px)

.label-edit
  &_viewing
    overflow-wrap: break-word
    word-break: break-word
    min-height: $field-height
    > *
      line-height: $field-height
  &__textarea-label
    overflow-y: auto
    line-height: initial !important
    padding: .4em
    word-break: break-word
    &_sm
      min-height: 4em
      max-height: 7em
    &_md
      min-height: 5.5em
      max-height: 10em
</style>

<script>
import { sanitizeToRender } from '@/services/stringHelper';

// Identificador para valor formatado não definido.
const UNSET_FORMATTED_VALUE = 'unset-formatted-value';

const EMPTY_VALUE_TO_SHOW = '-';

/**
 * Componente de edição/visualização.
 *
 * Consiste em um input que alterna entre modo de edição (input) e visualização (texto).
 *
 * Slots:
 * - padrão: é o input. Se omitido, criará um input de texto customizavel pela definição de
 *   inputOptions e inputEvents.
 * - "label": Opcional. É o texto em exibição quando o campo não está em edição. Por padrão, igual a
 *   value ou formattedValue.
 */
export default {
  props: {
    /**
     * Tipo do input.
     * Contém suporte para "textarea", que é um alias que quando informado, substitui o input
     * tradicional por um textarea e um modo de visualização com bloco de texto e rolagem.
     */
    inputType: {
      type: String,
      default: 'text',
    },
    /**
     * Valor do campo.
     * Também é emitido o evento input para compatibilidade com v-model.
     */
    value: null,
    /**
     * Informa se o campo está editável (com o input visível). Por padrão, o campo fica em
     * visualização
     */
    editable: {
      type: Boolean,
      default: false,
    },
    /**
     * Valor formatado para visualização. Se omitido, a visualização será igual a "value".
     */
    formattedValue: {
      type: String,
      default: UNSET_FORMATTED_VALUE,
    },
    /**
     * Opções/atributos para o input.
     */
    inputOptions: {
      type: [Object, Function],
      required: false,
      default: () => ({}),
    },
    /**
     * Eventos para o input.
     */
    inputEvents: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * Tamanho do textarea (caso em uso).
     * Valores:
     * - sm: 2 a 4 linhas
     * - md: 3 a 6 linhas (padrão)
     * - custom: não definido / a cargo do consumidor
     */
    textAreaHeight: {
      type: String,
      default: 'md',
    },
  },
  computed: {
    valueProxy: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    blockClasses() {
      return {
        'label-edit_viewing': !this.editable,
        'label-edit_editing': this.editable,
      };
    },
    showFormattedValue() {
      return this.formattedValue !== UNSET_FORMATTED_VALUE;
    },
    valueToShow() {
      const valueToShow = this.showFormattedValue ? this.formattedValue : this.value;

      if (valueToShow == null || valueToShow === '') {
        return EMPTY_VALUE_TO_SHOW;
      }

      if (typeof this.valueProxy !== 'string') {
        return valueToShow;
      }

      return sanitizeToRender(valueToShow);
    },
    isTextArea() {
      return this.inputType === 'textarea';
    },
    textAreaSizeOptions() {
      switch (this.textAreaHeight) {
        case 'sm':
          return {
            rows: 2,
            maxRows: 4,
          };
        case 'md':
          return {
            rows: 3,
            maxRows: 6,
          };
        default:
          return {};
      }
    },
    textAreaInputOptions() {
      return {
        ...this.inputOptions,
        ...this.textAreaSizeOptions,
      };
    },
    textAreaLabelClasses() {
      return {
        [`label-edit__textarea-label_${this.textAreaHeight}`]: this.textAreaHeight != null,
      };
    },
  },
};
</script>
